import { useState, useCallback, useMemo } from 'react'
import { Plus, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { TeamFormDialog } from '../components/TeamFormDialog'
import { TeamDeleteDialog } from '../components/TeamDeleteDialog'
import { TeamViewDialog } from '../components/TeamViewDialog'
import { useTeams, useDeleteTeam } from '../hooks'
import { createTeamColumns } from '../columns'
import { AdminRoute } from '@/components/auth/AdminRoute'
import type { Team } from '@/types/entities'

export function TeamListPage() {
    const { t } = useTranslation()
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)

    const { data: teamsResponse, isLoading } = useTeams()
    const deleteTeam = useDeleteTeam()

    const teams = teamsResponse?.data || []

    // Memoize callbacks to prevent re-creating on every render
    const handleEdit = useCallback((team: Team) => {
        setSelectedTeam(team)
        setShowEditDialog(true)
    }, [])

    const handleDelete = useCallback((team: Team) => {
        setSelectedTeam(team)
        setShowDeleteDialog(true)
    }, [])

    const handleView = useCallback((team: Team) => {
        setSelectedTeam(team)
        setShowViewDialog(true)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedTeam) {
            deleteTeam.mutate(selectedTeam.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedTeam(null)
                },
            })
        }
    }, [selectedTeam, deleteTeam])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export teams to CSV')
    }, [])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createTeamColumns(handleEdit, handleDelete, handleView, t),
        [handleEdit, handleDelete, handleView, t]
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('teams.loadingTeams')}</div>
            </div>
        )
    }

    return (
        <AdminRoute>
            <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold">{t('teams.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('teams.pageDescription')}
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <Button onClick={() => setShowCreateDialog(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('teams.createTeam')}
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-lg border bg-card p-6 md:col-start-2">
                        <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('teams.totalTeams')}</p>
                                <p className="text-2xl font-bold">{teams.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="max-w-4xl mx-auto">
                    <DataTable
                        columns={columns}
                        data={teams}
                        searchKey="teamName"
                        searchPlaceholder={t('teams.searchPlaceholder')}
                        onExport={handleExport}
                        exportButtonText={t('teams.export')}
                        enableRowSelection={false}
                    />
                </div>

                {/* Dialogs */}
                <TeamFormDialog
                    open={showCreateDialog}
                    onOpenChange={setShowCreateDialog}
                    team={null}
                    onCreated={(createdTeam) => {
                        setSelectedTeam(createdTeam)
                        setShowCreateDialog(false)
                        setShowEditDialog(true)
                    }}
                />

                <TeamFormDialog
                    open={showEditDialog}
                    onOpenChange={setShowEditDialog}
                    team={selectedTeam}
                />

                <TeamDeleteDialog
                    open={showDeleteDialog}
                    onOpenChange={setShowDeleteDialog}
                    team={selectedTeam}
                    onConfirm={handleDeleteConfirm}
                    isLoading={deleteTeam.isPending}
                />

                <TeamViewDialog
                    open={showViewDialog}
                    onOpenChange={setShowViewDialog}
                    team={selectedTeam}
                />
            </div>
        </AdminRoute>
    )
}
